import { ForwardRefExoticComponent, SVGProps } from 'react'

import { GroupBy } from '@factorialco/factorial-one-react/icons/app'
import { FormsElementInput, FormsElementTypeEnum } from 'generated/resources/datatable.graphql'

import { Element } from '../Element'

/**
 * SectionElement class for grouping other elements
 */
export class SectionElement extends Element {
  /**
   * Collection of elements in the section
   */
  elements: Element[] = []

  /**
   * Get the type of the element
   */
  getType(): FormsElementTypeEnum {
    return 'section'
  }

  /**
   * Get the icon for the element
   */
  getIcon(): ForwardRefExoticComponent<SVGProps<SVGSVGElement>> {
    return GroupBy
  }

  /**
   * Add an element to the section
   *
   * @param element - The element to add
   */
  addElement(element: Element): void {
    element.sectionId = this.id
    element.position = this.elements.length
    this.elements.push(element)
    this.sortElements()
  }

  /**
   * Remove an element from the section
   *
   * @param elementId - The ID of the element to remove
   */
  removeElement(elementId: string): void {
    this.elements = this.elements.filter((element) => element.id !== elementId)
    this.updatePositions()
  }

  /**
   * Get an element by its ID
   *
   * @param elementId - The ID of the element to get
   * @returns The element with the specified ID, or undefined if not found
   */
  getElement(elementId: string): Element | undefined {
    return this.elements.find((element) => element.id === elementId)
  }

  /**
   * Sort elements by their position
   */
  sortElements(): void {
    this.elements.sort((a, b) => a.position - b.position)
  }

  /**
   * Update positions of all elements to ensure they are sequential
   */
  updatePositions(): void {
    this.elements.forEach((element, index) => {
      element.position = index
    })
  }

  /**
   * Validate the section and its child elements
   */
  validate(): boolean {
    if (!this.label.trim()) return false

    // Validate child elements
    return this.elements.every((el) => el.validate())
  }

  /**
   * Override `toBackend()` to include child elements
   */
  toBackend(): FormsElementInput {
    return {
      ...super.toBackend(),
      elements: this.elements.map((el) => el.toBackend()),
    }
  }
}
